import {
  Base,
  Circle,
  Collection,
  Feature,
  FeatureStatus,
  Fill,
  Map,
  Modify,
  Point,
  Stroke,
  Style,
  VectorLayer,
  VectorSource
} from '../../models'

import {v4 as uuidv4} from 'uuid'
import {GeometryType} from '../../enums/GeometryType'
import {IGeometryEditsDispatcher} from '../../interfaces/dispatchers'
import {IFeatureLayerInfo} from '../../interfaces/models'
import {IConfig, IdFeature, TypeKeyIdFeaturesPair} from '../../interfaces/reducers'
import {IMapEditingService} from '../../interfaces/services/IMapEditingService'
import {getLayerInfo} from '../../reducers/layerInfos'
import {MapService} from './MapService'

export class MapEditingService implements IMapEditingService {
  public map: Map
  protected config: IConfig
  protected geomDispatcher: IGeometryEditsDispatcher
  protected mapService: MapService
  constructor(config: IConfig, map: Map, geomDispatcher: IGeometryEditsDispatcher, mapService: MapService) {
    this.map = map
    this.mapService = mapService
    this.config = config
    this.geomDispatcher = geomDispatcher
  }
  protected vertexFeatureIdFieldName = '_vertex_feat_id'
  protected vertexMultiPolygonPartIdName = 'vertexMultiPolygonPartIdName'
  protected vertexMultiLinePartIdName = 'vertexMultiLinePartIdName'
  protected vertexPolygonRingIdName = 'vertexPolygonRingIdName'
  protected vertexLineStringIdName = 'vertexLineStringIdName'

  protected subscribtions: {[key: string]: (changes: TypeKeyIdFeaturesPair) => void} = {}
  protected featuresOfInterest: TypeKeyIdFeaturesPair = {}
  protected editingVertexLayers: {[id: string]: VectorLayer} = {}
  protected editingMiddleVertexLayers: {[id: string]: VectorLayer} = {}

  ensureEditingVertexLayer(layerInfo: IFeatureLayerInfo) {
    if (this.editingVertexLayers[layerInfo.sourceDescription.id]) {
      return
    }
    const editingVSource = new VectorSource()
    const onEndPointChange = ({feature}: {feature: Feature}) => {
      this.updateVertex(feature as Feature, layerInfo)
      this.updateNearMiddlePoints(feature as Feature, layerInfo, false)
    }
    editingVSource.addEvent('changefeature', onEndPointChange)
    const layer = new VectorLayer({
      source: editingVSource,
      style: new Style({
        image: new Circle({
          radius: 5,
          fill: new Fill({color: 'rgb(66, 128, 179)'}),
          stroke: new Stroke({
            color: 'rgb(0, 153, 255)',
            width: 3
          })
        })
      })
    })
    layer.setZIndex(1000)
    layer.moreInfo = {
      urls: [],
      guid: uuidv4(),
      type: 'edit',
      refreshOnExtentChange: false,
      isActive: true
    }
    this.map.addLayer(layer)
    const modify = new Modify({
      source: editingVSource as any
    })
    modify.on('modifyend', ({features}: any) => {
      this.notifySubscribers()
      this.toggleMiddleEvents(false)
    })
    modify.on('modifystart', ({features}: any) => {
      this.toggleMiddleEvents(true)
    })
    this.map.addInteraction(modify)
    this.editingVertexLayers[layerInfo.sourceDescription.id] = layer
  }
  inserting: boolean

  toggleMiddleEvents = (disable: boolean) => {
    for (const id in this.editingVertexLayers) {
      if (disable) {
        this.editingMiddleVertexLayers[id].getSource().disableEvents()
      } else {
        this.editingMiddleVertexLayers[id].getSource().enableEvents()
      }
    }
  }
  toggleEndPointEvents = (disable: boolean) => {
    for (const id in this.editingVertexLayers) {
      if (disable) {
        this.editingVertexLayers[id].getSource().disableEvents()
      } else {
        this.editingVertexLayers[id].getSource().enableEvents()
      }
    }
  }

  ensureEditingMiddleVertexLayer(layerInfo: IFeatureLayerInfo) {
    if (this.editingMiddleVertexLayers[layerInfo.sourceDescription.id]) {
      return
    }
    const editingVSource = new VectorSource()
    const onMiddleChange = ({feature}: any) => {
      if (!feature.getProperties()['inserted']) {
        editingVSource.un('changefeature' as any, onMiddleChange)
        feature.set('inserted', true)
        this.insertFeatVertex(feature as Feature, layerInfo)
        editingVSource.on('changefeature' as any, onMiddleChange)
      } else {
        this.updateVertex(feature as Feature, layerInfo)
      }
    }
    editingVSource.addEvent('changefeature', onMiddleChange)
    const layer = new VectorLayer({
      source: editingVSource,
      style: new Style({
        image: new Circle({
          radius: 3,
          fill: new Fill({color: 'rgb(66, 128, 179)'}),
          stroke: new Stroke({
            color: 'rgb(0, 153, 255)',
            width: 3
          })
        })
      })
    })
    layer.setZIndex(1000)
    layer.moreInfo = {
      urls: [],
      guid: uuidv4(),
      type: 'edit',
      refreshOnExtentChange: false,
      isActive: true
    }
    this.map.addLayer(layer)
    const modify = new Modify({
      source: editingVSource as any
    })
    const onModifyEnd = ({features}: any) => {
      modify.un('modifyend' as any, onModifyEnd)
      const feats = features.getArray()
      feats.forEach((f: any) => {
        if (f.getProperties()['inserted']) {
          editingVSource.removeFeature(f)
          this.editingVertexLayers[layerInfo.sourceDescription.id].getSource().addFeature(f as Feature)
          this.updateNearMiddlePoints(f, layerInfo, true)
        }
      })
      modify.on('modifyend', onModifyEnd)
      this.toggleEndPointEvents(false)
      this.notifySubscribers()
    }
    modify.on('modifyend' as any, onModifyEnd)

    modify.on('modifystart' as any, () => {
      this.toggleEndPointEvents(true)
    })
    this.map.addInteraction(modify)
    this.editingMiddleVertexLayers[layerInfo.sourceDescription.id] = layer
  }

  updateNearMiddlePoints(endPointVertex: Feature, layerInfo: IFeatureLayerInfo, add: boolean) {
    const isClosedGeometry =
      layerInfo.getGeometryType() == GeometryType.Polygon || layerInfo.getGeometryType() == GeometryType.MultiPolygon
    const allEndPoints: Feature[] = []
    this.editingVertexLayers[layerInfo.sourceDescription.id].getSource().forEachFeature((f: any) => {
      if (
        f.getProperties()[this.vertexFeatureIdFieldName] ==
          endPointVertex.getProperties()[this.vertexFeatureIdFieldName] &&
        f.getProperties()[this.vertexMultiPolygonPartIdName] ==
          endPointVertex.getProperties()[this.vertexMultiPolygonPartIdName] &&
        f.getProperties()[this.vertexPolygonRingIdName] == endPointVertex.getProperties()[this.vertexPolygonRingIdName]
      ) {
        allEndPoints.push(f as Feature)
      }
    })
    const endPointIndex = endPointVertex.getProperties()[this.vertexLineStringIdName]
    let prevEndPointVertex: Point
    let nextEndPointVertex: Point
    if (endPointIndex == allEndPoints.length - 1) {
      //only happens on closed geometries like polygon
      prevEndPointVertex = allEndPoints
        .find(p => p.getProperties()[this.vertexLineStringIdName] == endPointIndex - 1)!
        .getGeometry() as Point
      nextEndPointVertex = allEndPoints
        .find(p => p.getProperties()[this.vertexLineStringIdName] == 0)!
        .getGeometry() as Point
    } else if (endPointIndex == 0) {
      //only happens on closed geometries like polygon
      prevEndPointVertex = allEndPoints
        .find(p => p.getProperties()[this.vertexLineStringIdName] == allEndPoints.length - 1)!
        .getGeometry() as Point
      nextEndPointVertex = allEndPoints
        .find(p => p.getProperties()[this.vertexLineStringIdName] == endPointIndex + 1)!
        .getGeometry() as Point
    } else {
      prevEndPointVertex = allEndPoints
        .find(p => p.getProperties()[this.vertexLineStringIdName] == endPointIndex - 1)!
        .getGeometry() as Point
      nextEndPointVertex = allEndPoints
        .find(p => p.getProperties()[this.vertexLineStringIdName] == endPointIndex + 1)!
        .getGeometry() as Point
    }
    if (add) {
      this.editingMiddleVertexLayers[layerInfo.sourceDescription.id].getSource().clear()
      const feat =
        this.featuresOfInterest[layerInfo.sourceDescription.id][
          endPointVertex.getProperties()[this.vertexFeatureIdFieldName]
        ]
      const middleVertexes = this.getMiddleVertexes({[feat.getId()]: feat}, layerInfo)
      this.editingMiddleVertexLayers[layerInfo.sourceDescription.id].getSource().addFeatures(middleVertexes)
    } else {
      this.editingMiddleVertexLayers[layerInfo.sourceDescription.id].getSource().forEachFeature(f => {
        if (
          f.getProperties()[this.vertexFeatureIdFieldName] ==
            endPointVertex.getProperties()[this.vertexFeatureIdFieldName] &&
          f.getProperties()[this.vertexMultiPolygonPartIdName] ==
            endPointVertex.getProperties()[this.vertexMultiPolygonPartIdName] &&
          f.getProperties()[this.vertexPolygonRingIdName] ==
            endPointVertex.getProperties()[this.vertexPolygonRingIdName]
        ) {
          if (endPointIndex == allEndPoints.length - 1) {
            if (f.getProperties()[this.vertexLineStringIdName] == endPointIndex) {
              f.setGeometry(
                this.getMiddle(
                  prevEndPointVertex.getCoordinates() as any,
                  endPointVertex.getGeometry().getCoordinates() as any
                )
              )
            } else if (f.getProperties()[this.vertexLineStringIdName] == 0 && isClosedGeometry) {
              f.setGeometry(
                this.getMiddle(
                  endPointVertex.getGeometry().getCoordinates() as any,
                  nextEndPointVertex.getCoordinates() as any
                )
              )
            }
          } else if (endPointIndex == 0) {
            if (f.getProperties()[this.vertexLineStringIdName] == 1) {
              f.setGeometry(
                this.getMiddle(
                  endPointVertex.getGeometry().getCoordinates() as any,
                  nextEndPointVertex.getCoordinates() as any
                )
              )
            } else if (f.getProperties()[this.vertexLineStringIdName] == 0 && isClosedGeometry) {
              f.setGeometry(
                this.getMiddle(
                  prevEndPointVertex.getCoordinates() as any,
                  endPointVertex.getGeometry().getCoordinates() as any
                )
              )
            }
          } else {
            if (f.getProperties()[this.vertexLineStringIdName] == endPointIndex) {
              f.setGeometry(
                this.getMiddle(
                  prevEndPointVertex.getCoordinates() as any,
                  endPointVertex.getGeometry().getCoordinates() as any
                )
              )
            } else if (f.getProperties()[this.vertexLineStringIdName] == endPointIndex + 1) {
              f.setGeometry(
                this.getMiddle(
                  endPointVertex.getGeometry().getCoordinates() as any,
                  nextEndPointVertex.getCoordinates() as any
                )
              )
            }
          }
        }
      })
    }
  }

  notifySubscribers = () => {
    for (const key in this.subscribtions) {
      this.subscribtions[key](this.featuresOfInterest)
    }
  }

  incrementMiddleVertexes(layerInfo: IFeatureLayerInfo, vertex: Feature) {
    const vertexes = this.editingMiddleVertexLayers[layerInfo.sourceDescription.id].getSource().getFeatures()
    vertexes.forEach(v => {
      if (
        v.getProperties()[this.vertexFeatureIdFieldName] == vertex.getProperties()[this.vertexFeatureIdFieldName] &&
        v.getProperties()[this.vertexMultiPolygonPartIdName] ==
          vertex.getProperties()[this.vertexMultiPolygonPartIdName] &&
        v.getProperties()[this.vertexPolygonRingIdName] == vertex.getProperties()[this.vertexPolygonRingIdName] &&
        v.getProperties()[this.vertexLineStringIdName] > vertex.getProperties()[this.vertexLineStringIdName]
      ) {
        v.set(this.vertexLineStringIdName, v.getProperties()[this.vertexLineStringIdName] + 1)
      }
    })
  }

  insertFeatVertex = (vertex: Feature, layerInfo: IFeatureLayerInfo): void => {
    const vertexes = this.editingVertexLayers[layerInfo.sourceDescription.id].getSource().getFeatures()
    this.incrementMiddleVertexes(layerInfo, vertex)
    vertexes.forEach(v => {
      if (
        v.getProperties()[this.vertexFeatureIdFieldName] == vertex.getProperties()[this.vertexFeatureIdFieldName] &&
        v.getProperties()[this.vertexMultiPolygonPartIdName] ==
          vertex.getProperties()[this.vertexMultiPolygonPartIdName] &&
        v.getProperties()[this.vertexPolygonRingIdName] == vertex.getProperties()[this.vertexPolygonRingIdName] &&
        v.getProperties()[this.vertexLineStringIdName] >= vertex.getProperties()[this.vertexLineStringIdName]
      ) {
        v.set(this.vertexLineStringIdName, v.getProperties()[this.vertexLineStringIdName] + 1)
        this.updateVertex(v as any as Feature, layerInfo)
      }
    })
  }

  updateVertex = (vertex: Feature, layerInfo: IFeatureLayerInfo) => {
    if (layerInfo.getGeometryType() == GeometryType.Point) {
      this.updatePointFeatVertex(layerInfo, vertex)
    } else if (layerInfo.getGeometryType() == GeometryType.LineString) {
      this.updateLineStringFeatVertex(layerInfo, vertex)
    } else if (layerInfo.getGeometryType() == GeometryType.Polygon) {
      this.updatePolygonFeatVertex(layerInfo, vertex)
    } else if (layerInfo.getGeometryType() == GeometryType.MultiPolygon) {
      this.updateMultiPolygonFeatVertex(layerInfo, vertex)
    } else {
      throw new Error('not imp')
    }
  }

  updatePointFeatVertex = (layerInfo: IFeatureLayerInfo, vertex: Feature): void => {
    const feat =
      this.featuresOfInterest[layerInfo.sourceDescription.id][vertex.getProperties()[this.vertexFeatureIdFieldName]]
    feat.getGeometry().setCoordinates(vertex.getGeometry().getCoordinates() as any)
  }

  updateLineStringFeatVertex = (layerInfo: IFeatureLayerInfo, vertex: Feature): void => {
    const feat =
      this.featuresOfInterest[layerInfo.sourceDescription.id][vertex.getProperties()[this.vertexFeatureIdFieldName]]
    const geom = feat.getGeometry()
    const coordinates = geom.getCoordinates() as number[][]
    coordinates[vertex.getProperties()[this.vertexLineStringIdName]] = vertex.getGeometry().getCoordinates() as number[]
    feat.getGeometry().setCoordinates(coordinates as any)
  }

  updatePolygonFeatVertex = (layerInfo: IFeatureLayerInfo, vertex: Feature): void => {
    const feat =
      this.featuresOfInterest[layerInfo.sourceDescription.id][vertex.getProperties()[this.vertexFeatureIdFieldName]]
    const geom = feat.getGeometry()
    const coordinates = geom.getCoordinates() as number[][][]
    coordinates[vertex.getProperties()[this.vertexPolygonRingIdName]][
      vertex.getProperties()[this.vertexLineStringIdName]
    ] = vertex.getGeometry().getCoordinates() as number[]
    feat.getGeometry().setCoordinates(coordinates as any)
  }

  updateMultiPolygonFeatVertex = (layerInfo: IFeatureLayerInfo, vertex: Feature): void => {
    const feat =
      this.featuresOfInterest[layerInfo.sourceDescription.id][vertex.getProperties()[this.vertexFeatureIdFieldName]]
    const geom = feat.getGeometry()
    const coordinates = geom.getCoordinates() as number[][][][]
    coordinates[vertex.getProperties()[this.vertexMultiPolygonPartIdName]][
      vertex.getProperties()[this.vertexPolygonRingIdName]
    ][vertex.getProperties()[this.vertexLineStringIdName]] = vertex.getGeometry().getCoordinates() as number[]
    feat.getGeometry().setCoordinates(coordinates as any)
  }

  public unsubscribeForChanges(key: string) {
    delete this.subscribtions[key]
  }

  public subscribeForChanges(key: string, subFunction: (changes: TypeKeyIdFeaturesPair) => void) {
    this.subscribtions[key] = subFunction
  }

  protected getEmptyVertex(feature: Feature): Feature {
    const feat = new Feature()
    feat.setProperties({[this.vertexFeatureIdFieldName]: feature.getId()})
    return feat
  }

  protected getPointVertexes(feature: Feature): Feature[] {
    const feat = this.getEmptyVertex(feature)
    feat.setProperties({...feat.getProperties()})
    feat.setGeometry(feature.getGeometry().clone())
    return [feat]
  }

  protected getLineStringVertexes(feature: Feature): Feature[] {
    const vertexes: Feature[] = []
    const coords = feature.getGeometry().getCoordinates() as number[][]
    coords.forEach((c, i) => {
      const feat = this.getEmptyVertex(feature)
      feat.setProperties({
        ...feat.getProperties(),
        [this.vertexLineStringIdName]: i
      })
      feat.setGeometry(new Point(c))
      vertexes.push(feat)
    })
    return vertexes
  }

  protected getPolygonVertexes(feature: Feature): Feature[] {
    const vertexes: Feature[] = []
    const rings = feature.getGeometry().getCoordinates() as number[][][]
    rings.forEach((coords, r) => {
      coords.forEach((c, i) => {
        const feat = this.getEmptyVertex(feature)
        feat.setProperties({
          ...feat.getProperties(),
          [this.vertexPolygonRingIdName]: r,
          [this.vertexLineStringIdName]: i
        })
        feat.setGeometry(new Point(c))
        vertexes.push(feat)
      })
    })
    return vertexes
  }
  protected getMultiPolygonVertexes(feature: Feature): Feature[] {
    const vertexes: Feature[] = []
    const polys = feature.getGeometry().getCoordinates() as number[][][][]
    polys.forEach((rings, p) => {
      rings.forEach((coords, r) => {
        coords.forEach((c, i) => {
          const feat = this.getEmptyVertex(feature)
          feat.setProperties({
            ...feat.getProperties(),
            [this.vertexMultiPolygonPartIdName]: p,
            [this.vertexPolygonRingIdName]: r,
            [this.vertexLineStringIdName]: i
          })
          feat.setGeometry(new Point(c))
          vertexes.push(feat)
        })
      })
    })
    return vertexes
  }

  cursorPointerEvent: any
  pointerEvent = (event: any) => {
    const hit = this.map.forEachFeatureAtPixel(event.pixel, function (feature: any, layer: any) {
      return !!layer && layer.moreInfo.type == 'edit'
    })
    if (hit) {
      this.mapService.mapDiv.style.cursor = 'pointer'
    } else {
      this.mapService.mapDiv.style.cursor = 'default'
    }
  }
  trackingCursor = false
  public startCursorPointer() {
    if (!this.trackingCursor) {
      this.trackingCursor = true
      this.cursorPointerEvent = this.map.on('pointermove', this.pointerEvent)
    }
  }

  public stopCursorPointer() {
    this.trackingCursor = false
    this.mapService.mapDiv.style.cursor = 'default'
    this.map.un('pointermove', this.pointerEvent)
  }

  protected _clearFeaturesVertexes(id: string, features: TypeKeyIdFeaturesPair) {
    if (this.editingVertexLayers[id]) {
      const source = this.editingVertexLayers[id].getSource()
      for (const f in features[id]) {
        const guid = features[id][f].guid
        source.getFeatures().forEach(fea => {
          const sourceFeatId = fea.getProperties()[this.vertexFeatureIdFieldName]
          if (sourceFeatId == f || sourceFeatId == guid) {
            source.removeFeature(fea)
          }
        })
      }
      const midsource = this.editingMiddleVertexLayers[id].getSource()
      for (const f in features[id]) {
        const guid = features[id][f].guid
        midsource.getFeatures().forEach(fea => {
          const sourceFeatId = fea.getProperties()[this.vertexFeatureIdFieldName]
          if (sourceFeatId == f || sourceFeatId == guid) {
            midsource.removeFeature(fea)
          }
        })
      }
    }
  }

  protected clearFeaturesVertexes(features: TypeKeyIdFeaturesPair) {
    for (const id in features) {
      this._clearFeaturesVertexes(id, features)
    }
  }

  protected getVertexes(features: IdFeature, layerInfo: IFeatureLayerInfo): Feature[] {
    let vertexes: Feature[] = []
    for (const fId in features) {
      const feat = features[fId]
      if (feat.getGeometry()) {
        if (layerInfo.getGeometryType() == GeometryType.Point) {
          vertexes = vertexes.concat(this.getPointVertexes(feat))
        } else if (layerInfo.getGeometryType() == GeometryType.LineString) {
          vertexes = vertexes.concat(this.getLineStringVertexes(feat))
        } else if (layerInfo.getGeometryType() == GeometryType.Polygon) {
          vertexes = vertexes.concat(this.getPolygonVertexes(feat))
        } else if (layerInfo.getGeometryType() == GeometryType.MultiPolygon) {
          vertexes = vertexes.concat(this.getMultiPolygonVertexes(feat))
        } else {
          throw new Error('not impl')
        }
      }
    }

    return vertexes
  }

  protected getMiddle(coord1: number[], coord2: number[]): Point {
    return new Point([coord1[0] + (coord2[0] - coord1[0]) / 2, coord1[1] + (coord2[1] - coord1[1]) / 2])
  }

  protected getLineStringMiddleVertexes(feature: Feature): Feature[] {
    const vertexes: Feature[] = []
    const coords = feature.getGeometry().getCoordinates() as number[][]
    for (let i = 0; i < coords.length - 1; i++) {
      const feat = this.getEmptyVertex(feature)
      feat.setProperties({
        ...feat.getProperties(),
        [this.vertexLineStringIdName]: i + 1
      })
      feat.setGeometry(this.getMiddle(coords[i], coords[i + 1]))
      vertexes.push(feat)
    }
    return vertexes
  }

  protected getPolygonMiddleVertexes(feature: Feature): Feature[] {
    const vertexes: Feature[] = []
    const rings = feature.getGeometry().getCoordinates() as number[][][]
    rings.forEach((coords, r) => {
      if (coords.length > 0) {
        const feat = this.getEmptyVertex(feature)
        feat.setProperties({
          ...feat.getProperties(),
          [this.vertexPolygonRingIdName]: r,
          [this.vertexLineStringIdName]: 0
        })
        feat.setGeometry(this.getMiddle(coords[coords.length - 1], coords[0]))
        vertexes.push(feat)
        for (let i = 0; i < coords.length - 1; i++) {
          const feat = this.getEmptyVertex(feature)
          feat.setProperties({
            ...feat.getProperties(),
            [this.vertexPolygonRingIdName]: r,
            [this.vertexLineStringIdName]: i + 1
          })
          feat.setGeometry(this.getMiddle(coords[i], coords[i + 1]))
          vertexes.push(feat)
        }
      }
    })
    return vertexes
  }
  protected getMultiPolygonMiddleVertexes(feature: Feature): Feature[] {
    const vertexes: Feature[] = []
    const polys = feature.getGeometry().getCoordinates() as number[][][][]
    polys.forEach((rings, p) => {
      rings.forEach((coords, r) => {
        if (coords.length > 0) {
          const feat = this.getEmptyVertex(feature)
          feat.setProperties({
            ...feat.getProperties(),
            [this.vertexMultiPolygonPartIdName]: p,
            [this.vertexPolygonRingIdName]: r,
            [this.vertexLineStringIdName]: 0
          })
          feat.setGeometry(this.getMiddle(coords[coords.length - 1], coords[0]))
          vertexes.push(feat)
          for (let i = 0; i < coords.length - 1; i++) {
            const feat = this.getEmptyVertex(feature)
            feat.setProperties({
              ...feat.getProperties(),
              [this.vertexMultiPolygonPartIdName]: p,
              [this.vertexPolygonRingIdName]: r,
              [this.vertexLineStringIdName]: i + 1
            })
            feat.setGeometry(this.getMiddle(coords[i], coords[i + 1]))
            vertexes.push(feat)
          }
        }
      })
    })
    return vertexes
  }
  protected getMiddleVertexes(features: IdFeature, layerInfo: IFeatureLayerInfo): Feature[] {
    let vertexes: Feature[] = []
    for (const fId in features) {
      const feat = features[fId]
      if (feat.getGeometry()) {
        if (layerInfo.getGeometryType() == GeometryType.Point) {
          // vertexes = vertexes.concat(this.getPointVertexes(feat)) // there is no need for middle
        } else if (layerInfo.getGeometryType() == GeometryType.LineString) {
          vertexes = vertexes.concat(this.getLineStringMiddleVertexes(feat))
        } else if (layerInfo.getGeometryType() == GeometryType.Polygon) {
          vertexes = vertexes.concat(this.getPolygonMiddleVertexes(feat))
        } else if (layerInfo.getGeometryType() == GeometryType.MultiPolygon) {
          vertexes = vertexes.concat(this.getMultiPolygonMiddleVertexes(feat))
        } else {
          throw new Error('not impl')
        }
      }
    }

    return vertexes
  }

  public syncSource(features: IdFeature, layerInfo: IFeatureLayerInfo) {
    //removeing may be done with force parameter so it can be optional. this way an original will be preserved on the map
    if (layerInfo.vectorLayer) {
      const source = layerInfo.vectorLayer.getSource()
      for (const featId in features) {
        if (source.getFeatureById(featId)) {
          source.removeFeature(source.getFeatureById(featId) as any)
        }
        if (features[featId].status != FeatureStatus.Deleted) {
          source.addFeature(features[featId])
        } else {
          this._clearFeaturesVertexes(layerInfo.sourceDescription.id, {[layerInfo.sourceDescription.id]: features})
        }
      }
    }
  }

  protected appendEditingFeatures(features: TypeKeyIdFeaturesPair) {
    for (const id in features) {
      if (!this.featuresOfInterest[id]) {
        this.featuresOfInterest[id] = {}
      }
      for (const f in features[id]) {
        this.featuresOfInterest[id][f] = features[id][f]
      }
    }
  }

  public setForEditing(features: TypeKeyIdFeaturesPair) {
    this.update(features)
    this.mapService.onStartInteraction('editing')
    this.startCursorPointer()
  }
  protected update(features: TypeKeyIdFeaturesPair) {
    this.appendEditingFeatures(features)
    for (const id in features) {
      const info = getLayerInfo(id)
      if (info && info.vectorLayer) {
        this.ensureEditingVertexLayer(info)
        this.ensureEditingMiddleVertexLayer(info)
        this._clearFeaturesVertexes(id, features)
        this.syncSource(features[id], info)
        const vertexes = this.getVertexes(features[id], info)
        this.editingVertexLayers[id].getSource().addFeatures(vertexes)
        const middleVertexes = this.getMiddleVertexes(features[id], info)
        this.editingMiddleVertexLayers[id].getSource().addFeatures(middleVertexes)
      } else {
        throw new Error(`Setting editing on non exisitng layer with id: ${id}`)
      }
    }
  }
  protected clearEditingVertexSources() {
    for (const id in this.editingVertexLayers) {
      this.editingVertexLayers[id].getSource().clear()
    }
  }

  public ejectFromEditing(features: TypeKeyIdFeaturesPair) {
    this.clearFeaturesVertexes(features)
    for (const id in features) {
      for (const f in features[id]) {
        if (this.featuresOfInterest) {
          delete this.featuresOfInterest[id][f] //delete updated or deleted features
          delete this.featuresOfInterest[id][features[id][f].guid] //delete adds - they change getId() so they were previuosly using guid
        }
      }
      if (Object.keys(this.featuresOfInterest[id]).length == 0) {
        delete this.featuresOfInterest[id]
      }
    }
    if (Object.keys(this.featuresOfInterest).length == 0) {
      this.deactivateEdits()
    }
  }

  public externalUpdate(changes: TypeKeyIdFeaturesPair) {
    this.update(changes)
    this.notifySubscribers()
  }
  public deactivateEdits(): void {
    this.stopCursorPointer()
    this.mapService.onEndInteraction('editing')
    this.clearEditingVertexSources()
    this.featuresOfInterest = {}
  }
}
